// src/components/SignUp.js

import React, { useState } from 'react';
import './SignUp.css';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import api from '../api';
import { Link } from 'react-router-dom';
import { signInWithGoogle, isSignInDismissed } from '../auth/googleSignIn';
import {
  PRIVACY_URL, TERMS_URL, clearPendingSignup, savePendingSignup, signupPayload, pendingSignupFor,
} from '../auth/pendingSignup';

const MIN_PASSWORD = 8;

const FIREBASE_ERRORS = {
  'auth/email-already-in-use': 'An account already exists with this email. Sign in instead.',
  'auth/invalid-email': 'That email address doesn’t look right.',
  'auth/weak-password': `Use a password of at least ${MIN_PASSWORD} characters.`,
  'auth/account-exists-with-different-credential': 'An account already exists with this email. Sign in instead.',
  'auth/network-request-failed': 'Network problem. Check your connection and try again.',
};

const GoogleIcon = () => (
  <svg className="method-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const SignUp = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedEstimates, setAcceptedEstimates] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const consentsGiven = acceptedEstimates && acceptedTerms;
  const busy = submitting || googleLoading;

  const details = () => ({
    name: name.trim(), email, companyName: companyName.trim(), phone: phone.trim(), acceptedTerms, acceptedEstimates,
  });

  const missingForGoogle = () => {
    if (!companyName.trim()) return 'Add your company name first; Google fills in your name and email.';
    if (!consentsGiven) return 'Tick both boxes first.';
    return '';
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!companyName.trim()) { setError('Add your company name.'); return; }
    if (password.length < MIN_PASSWORD) { setError(`Use a password of at least ${MIN_PASSWORD} characters.`); return; }
    if (!consentsGiven) { setError('Tick both boxes to continue.'); return; }

    setSubmitting(true);
    // Before the account exists: the auth listener's first /firebase-login call reads these.
    savePendingSignup(details());
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await updateProfile(user, { displayName: name.trim() });
      const idToken = await user.getIdToken(true);
      const pending = pendingSignupFor(user);
      await api.post('/firebase-login', {
        idToken, name: name.trim(), ...(pending ? { signup: signupPayload(pending) } : {}),
      }, { withCredentials: true });
      clearPendingSignup();
      // Navigation is handled by onAuthStateChanged in App.jsx
    } catch (err) {
      console.error('Sign-Up Error:', err);
      clearPendingSignup();
      setError(FIREBASE_ERRORS[err.code] || 'Something went wrong creating your account. Please try again.');
      setSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    const missing = missingForGoogle();
    if (missing) { setError(missing); return; }
    setError('');
    setGoogleLoading(true);
    // Google may redirect away and back; the details wait in the session for App.jsx.
    savePendingSignup({ ...details(), name: '', email: '' });
    try {
      await signInWithGoogle();
    } catch (err) {
      setGoogleLoading(false);
      if (isSignInDismissed(err)) return;
      console.error('Google Sign-In Error:', err);
      clearPendingSignup();
      setError(FIREBASE_ERRORS[err.code] || 'Google sign-up didn’t finish. Please try again.');
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-brand">
          <img className="signup-brand-logo" src="/realtorch-brand-name-logo.svg" alt="RealTorch" />
          <div className="brand-divider" />
        </div>

        <h2 className="signup-title">Start your free trial</h2>
        <p className="signup-subtitle">10 free Atlas messages. No credit card.</p>

        <form onSubmit={handleSignUp}>
          <div className="signup-row">
            <div className="form-field">
              <label htmlFor="name">Your name</label>
              <input id="name" name="name" type="text" autoComplete="name" placeholder="Jane Doe"
                value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div className="form-field">
              <label htmlFor="company">Company name</label>
              <input id="company" name="company" type="text" autoComplete="organization" placeholder="Acme Homes"
                value={companyName} onChange={(e) => setCompanyName(e.target.value)} required maxLength={200} />
            </div>
          </div>
          <div className="signup-row">
            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" placeholder="you@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="phone">Phone number <span className="label-optional">(optional)</span></label>
              <input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="(555) 555-5555"
                value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="password">Password</label>
            <div className="password-field">
              <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD} characters`} value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={MIN_PASSWORD} />
              <button type="button" className="password-toggle" onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <label className="signup-check">
            <input type="checkbox" checked={acceptedEstimates} onChange={(e) => setAcceptedEstimates(e.target.checked)} />
            <span>
              I understand RealTorch’s data and Atlas’s answers are <strong>estimates</strong> that can be wrong or out of
              date, and <strong>I’ll verify anything I rely on</strong> before buying land, pricing or building.
            </span>
          </label>
          <label className="signup-check">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
            <span>
              I agree to the <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a> and
              acknowledge the <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </label>

          {error && (
            <div className="signup-error" role="alert">
              {error}
              {error.includes('Sign in') && <> <Link to="/login">Go to sign in</Link></>}
            </div>
          )}

          <div className="signup-actions">
            <button type="submit" className="btn-submit" disabled={busy || !consentsGiven}>
              {submitting ? 'Creating account…' : 'Create account →'}
            </button>
            <button type="button" className="btn-method btn-method-google" onClick={handleGoogleSignUp} disabled={busy}>
              <GoogleIcon />
              {googleLoading ? 'Signing up…' : 'Sign up with Google'}
            </button>
          </div>
        </form>

        <div className="signup-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
